import { useEffect, useState } from "react";
import { api, CURRENCY_SYMBOL } from "../lib/api";
import Layout from "../components/Layout";
import { PageHeader } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CurrencyGbp, DownloadSimple, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

const CURRENCIES = ["GBP", "USD", "EUR", "TRY"];
const NAMES = { GBP: "İngiliz Sterlini", USD: "ABD Doları", EUR: "Euro", TRY: "Türk Lirası" };

export default function ExchangeRates() {
  const [rates, setRates] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/exchange-rates");
      setRates(res.data.rates || {});
      setUpdatedAt(res.data.updated_at);
    } catch (e) { toast.error("Kurlar yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      CURRENCIES.forEach((c) => { clean[c] = parseFloat(rates[c]) || 0; });
      clean.GBP = 1;
      const res = await api.put("/exchange-rates", { rates: clean });
      setRates(res.data.rates); setUpdatedAt(res.data.updated_at);
      toast.success("Kurlar kaydedildi");
    } catch (e) { toast.error("Kaydetme hatası"); }
    finally { setSaving(false); }
  };

  const fetchLive = async () => {
    setFetching(true);
    try {
      const res = await api.post("/exchange-rates/fetch");
      setRates(res.data.rates); setUpdatedAt(res.data.updated_at);
      toast.success("Güncel kurlar alındı");
    } catch (e) { toast.error("Güncel kur alınamadı, lütfen manuel girin"); }
    finally { setFetching(false); }
  };

  if (loading) return <Layout><div className="h-96 flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></Layout>;

  return (
    <Layout>
      <PageHeader
        title="Döviz Kurları"
        subtitle="Ana para biriminiz GBP. Diğer birimlerin GBP karşılığını girin."
        action={
          <Button variant="outline" data-testid="fetch-rates-button" onClick={fetchLive} disabled={fetching} className="gap-2">
            <DownloadSimple size={18} className={fetching ? "animate-pulse" : ""} />
            {fetching ? "Alınıyor..." : "Güncel Kurları Çek"}
          </Button>
        }
      />

      <div className="bg-card border border-border rounded-md p-6 max-w-2xl" data-testid="rates-panel">
        <div className="flex items-center gap-2 mb-6">
          <CurrencyGbp size={22} className="text-primary" />
          <div>
            <h2 className="font-display text-lg font-semibold">1 birim = ? GBP</h2>
            {updatedAt && <p className="text-xs text-muted-foreground">Son güncelleme: {new Date(updatedAt).toLocaleString("tr-TR")}</p>}
          </div>
        </div>

        <div className="space-y-4">
          {CURRENCIES.map((c) => (
            <div key={c} className="flex items-center gap-4" data-testid={`rate-row-${c}`}>
              <div className="w-32 flex items-center gap-2">
                <span className="font-mono font-semibold w-8">{CURRENCY_SYMBOL[c]}</span>
                <div>
                  <p className="font-mono text-sm font-medium">{c}</p>
                  <p className="text-xs text-muted-foreground">{NAMES[c]}</p>
                </div>
              </div>
              <div className="flex-1">
                <Label className="sr-only">{c} kuru</Label>
                <Input
                  data-testid={`rate-input-${c}`}
                  type="number"
                  step="0.0001"
                  value={rates[c] ?? ""}
                  disabled={c === "GBP"}
                  onChange={(e) => setRates({ ...rates, [c]: e.target.value })}
                  className="font-mono"
                />
              </div>
              <span className="text-sm text-muted-foreground w-10">GBP</span>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-6 border-t border-border flex justify-end">
          <Button data-testid="save-rates-button" onClick={save} disabled={saving} className="gap-2">
            <FloppyDisk size={18} /> {saving ? "Kaydediliyor..." : "Kurları Kaydet"}
          </Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mt-4 max-w-2xl">
        Örnek: 1 USD = 0.79 GBP ise USD satırına <span className="font-mono">0.79</span> yazın. Genel Bakış'taki tüm tutarlar bu kurlarla GBP'ye çevrilir.
      </p>
    </Layout>
  );
}
